// promptAttachments — the ONE way a prompt composer turns its dropped
// attachments into something the AI actually receives.
//
// Why this exists in shared/ rather than inside the composer: the composer
// component lives in packages/canvas (CanvasPromptTextarea._sendPrompt), but
// the only LIVE mount of it is the workspace shell's override in
// packages/workspace/app.js, which replaces the send handlers to route through
// `homeAiSend` instead. That override read `state.draft` and nothing else — so
// a dropped file/ref/image rendered a chip, was ignored on send, and was never
// even cleared afterwards (it stayed in the gallery looking sent). Two send
// paths, two different ideas of what a prompt body is, and the one users
// actually touch was the broken one. Packages can't import from each other
// (FA001), so the single source has to live here and be reached via el.call.
//
// TRANSMISSION REALITY — do not overstate this. The local-agent bridge ships
// `{type:'user_message', text, context, attachments}`, but its receiving
// handler currently reads only `text`/`context`. So the CONTEXT LINES below
// are the only part guaranteed to reach the agent today; `attachments` (which
// carries real image bytes) leaves the browser intact but is dropped on the
// far side until the bridge forwards it. That's why images get a text line
// too, rather than relying on the byte payload alone.

// Build the outgoing prompt body + attachment payload from a composer's own
// state. Returns { body, attachments, isEmpty }.
//
// `isEmpty` is the guard the caller should use INSTEAD of testing the draft
// alone: a drop containing only an image and no typed text is a real prompt,
// and testing `!draft` alone silently submitted nothing.
//
// `el` is needed only to resolve dragRefToText through el.call (refs render as
// markdown links so the agent gets a working deep link back to the entity).
export const buildPromptPayload = function (el, st) {
  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer, pure helper — see longPress.js for the identical shape).
  const _fmtBytes = (n) => {
    if (n == null || Number.isNaN(n)) return ''
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
  }

  const draft = ((st && st.draft) || '').trim()
  const files = (st && st.files) || []
  const refs = (st && st.refs) || []
  const images = (st && st.images) || []

  const contextLines = [
    ...refs.map((ref) => {
      try {
        return el.call('dragRefToText', ref)
      } catch (_) {
        // Unregistered el.call returns undefined silently; fall back to
        // something readable rather than emitting "undefined" into the prompt.
        return (ref && (ref.title || ref.href)) || ''
      }
    }),
    ...files.map((f) => `[Attached file: ${f.name} (${f.mime}, ${_fmtBytes(f.size)})]`),
    // Images may be legacy bare data-URL strings from an older composer state
    // shape; those carry no metadata, so describe them generically instead of
    // emitting "[Attached image: undefined (undefined, )]".
    ...images.map((img) =>
      img && typeof img === 'object' && img.name
        ? `[Attached image: ${img.name} (${img.mime}, ${_fmtBytes(img.size)})]`
        : '[Attached image]'
    )
  ].filter(Boolean)

  const body = contextLines.length ? [draft, ...contextLines].filter(Boolean).join('\n') : draft

  // Only real image objects carry bytes worth forwarding.
  const attachments = images
    .filter((img) => img && typeof img === 'object' && img.dataUrl)
    .map((img) => ({ name: img.name, mime: img.mime, size: img.size, dataUrl: img.dataUrl }))

  return {
    body,
    attachments,
    isEmpty: !draft && !files.length && !refs.length && !images.length
  }
}

// The state patch that clears a composer after a successful send. Kept beside
// the builder so a caller can't clear the draft and forget the attachments —
// the exact bug this module was written to fix.
export const clearedPromptState = () => ({ draft: '', files: [], refs: [], images: [] })

// removePromptAttachment({ kind: 'ref'|'file'|'image', key }) — the ONE removal
// path for a composer attachment (O1 AiContextChip removeVia descriptor). `this`
// is the chip (or any node inside the composer subtree); walks up to the nearest
// element whose state carries the draft bag (`images` is the discriminator the
// composer's own state always declares) and filters the matching list. Replaces
// PromptAttachmentChip.RemoveBtn's inline walk-up.
export const removePromptAttachment = function (desc) {
  if (!desc || !desc.kind || desc.key == null) return false
  let host = this
  while (host && host.parent && !(host.state && host.state.images)) host = host.parent
  const st = host && host.state
  if (!st || typeof st.update !== 'function') return false
  const listKey = desc.kind === 'ref' ? 'refs' : desc.kind === 'file' ? 'files' : 'images'
  const list = Array.isArray(st[listKey]) ? st[listKey] : []
  st.update({ [listKey]: list.filter((x) => !x || x.key !== desc.key) })
  return true
}

// removePickedElement(pickedAt) — drop ONE picked (pointed-at) element from
// root.pickedElements by its pickedAt stamp; `null`/undefined clears them all.
// Replaces PickedContextChip's inline filter + "Clear all".
export const removePickedElement = function (pickedAt) {
  const el = this
  const root =
    (el && el.state && el.state.root) || (el && el.getRoot && el.getRoot()?.state) || null
  if (!root || typeof root.update !== 'function') return false
  const list = Array.isArray(root.pickedElements) ? root.pickedElements : []
  root.update({
    pickedElements: pickedAt == null ? [] : list.filter((p) => p && p.pickedAt !== pickedAt)
  })
  return true
}
